import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { CheckCircle2 } from "lucide-react";
import { SEO } from "@/components/SEO";
import { ServiceAreasSection } from "@/components/CrossLinks";

export default function Repair() {
  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: "https://www.saskroofingcompany.com/",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Services",
          item: "https://www.saskroofingcompany.com/services",
        },
        {
          "@type": "ListItem",
          position: 3,
          name: "Roof Repair",
          item: "https://www.saskroofingcompany.com/services/repair",
        },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "@id": "https://www.saskroofingcompany.com/services/repair#service",
      serviceType: "Roof Repair",
      name: "Roof Repair",
      description:
        "Fast and reliable roof repair in Saskatchewan. We fix leaks, missing shingles, and structural damage before it spreads.",
      url: "https://www.saskroofingcompany.com/services/repair",
      provider: {
        "@id": "https://www.saskroofingcompany.com/#localbusiness",
        "@type": "RoofingContractor",
        name: "Sask Roofing Company",
        url: "https://www.saskroofingcompany.com/",
        telephone: "+1-639-739-7969",
      },
      areaServed: [
        {
          "@type": "City",
          name: "Saskatoon",
          containedInPlace: { "@type": "State", name: "Saskatchewan" },
        },
        {
          "@type": "City",
          name: "Regina",
          containedInPlace: { "@type": "State", name: "Saskatchewan" },
        },
        {
          "@type": "City",
          name: "Moose Jaw",
          containedInPlace: { "@type": "State", name: "Saskatchewan" },
        },
        {
          "@type": "City",
          name: "Prince Albert",
          containedInPlace: { "@type": "State", name: "Saskatchewan" },
        },
        {
          "@type": "City",
          name: "Swift Current",
          containedInPlace: { "@type": "State", name: "Saskatchewan" },
        },
        {
          "@type": "City",
          name: "Yorkton",
          containedInPlace: { "@type": "State", name: "Saskatchewan" },
        },
        {
          "@type": "City",
          name: "Estevan",
          containedInPlace: { "@type": "State", name: "Saskatchewan" },
        },
        {
          "@type": "City",
          name: "Weyburn",
          containedInPlace: { "@type": "State", name: "Saskatchewan" },
        },
      ],
      offers: {
        "@type": "AggregateOffer",
        priceCurrency: "CAD",
        lowPrice: "300",
        highPrice: "3000",
      },
      availableChannel: {
        "@type": "ServiceChannel",
        serviceUrl: "https://www.saskroofingcompany.com/contact",
        servicePhone: {
          "@type": "ContactPoint",
          telephone: "+1-639-739-7969",
          contactType: "customer service",
          areaServed: "CA-SK",
          availableLanguage: "English",
        },
      },
      termsOfService: "https://www.saskroofingcompany.com/about#warranty",
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: "4.9",
        reviewCount: "87",
        bestRating: "5",
        worstRating: "1",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "How quickly can you fix a leaking roof?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "We prioritize active leaks and can typically dispatch a repair crew within 24-48 hours. We also offer emergency tarping to stop immediate water ingress.",
          },
        },
        {
          "@type": "Question",
          name: "Why is my roof leaking in the winter?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Winter leaks in Saskatchewan are often caused by ice damming—where melting snow refreezes at the eaves, pushing water back up under the shingles.",
          },
        },
        {
          "@type": "Question",
          name: "Can you repair a roof that is 20 years old?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "It depends on the condition of the surrounding shingles. If they are extremely brittle, attempting a repair might cause more damage. We will assess the roof and give you an honest recommendation on whether repair or replacement is the better investment.",
          },
        },
      ],
    },
  ];

  return (
    <div className="flex flex-col min-h-screen">
      <SEO
        title="Roof Repair Saskatchewan | Fast Leak & Shingle Repair"
        description="Reliable roof repair in Saskatchewan. We diagnose and fix leaks, missing shingles, and ice dam damage quickly to protect your home."
        schema={schema}
      />

      <section className="relative py-32 text-primary-foreground overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/a45bf747-d0d6-427b-adfb-d9ba7d5beb9b.png&output=webp&q=60"
            alt="Roof Repair"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/40"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
        </div>
        <div className="container relative px-4 z-10">
          <div className="max-w-3xl space-y-6">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white drop-shadow-md">
              Roof Repair Saskatchewan
            </h1>
            <p className="text-xl text-white/90 drop-shadow-sm font-medium">
              Don't let a small leak turn into major structural damage. We
              provide fast, accurate diagnostics and reliable roof repairs
              across Saskatchewan.
            </p>
            <div className="pt-4">
              <Button
                size="lg"
                className="bg-accent text-accent-foreground hover:bg-accent/90"
                asChild
              >
                <Link to="/contact">Request a Roof Repair</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-background">
        <div className="container px-4">
          <div className="grid md:grid-cols-2 gap-12">
            <div className="space-y-6">
              <h2 className="text-3xl font-bold">When You Need Roof Repair</h2>
              <p className="text-lg text-muted-foreground">
                You need roof repair immediately if you notice water stains on
                your ceiling, missing or torn shingles after a windstorm,
                granules accumulating in your gutters, or visible sagging on the
                roof deck.
              </p>
              <h3 className="text-2xl font-bold pt-4">How It Works</h3>
              <p className="text-lg text-muted-foreground">
                We don't just patch the symptom; we find the source. Water can
                travel far from the actual entry point. We trace the leak,
                remove the damaged materials, repair the underlying decking if
                necessary, and install new underlayment and matching shingles to
                restore the watertight seal.
              </p>
            </div>
            <div className="bg-muted/30 p-8 rounded-xl border">
              <h3 className="text-2xl font-bold mb-6">
                Why It Suits Saskatchewan
              </h3>
              <ul className="space-y-4">
                {[
                  "Expertise in diagnosing winter ice dam leaks common in our climate.",
                  "Fast response to high-wind blow-offs common on the prairies.",
                  "We use sealants specifically rated for extreme cold applications.",
                  "Honest assessments—we won't push a full replacement if a repair will genuinely fix the issue.",
                ].map((item, i) => (
                  <li key={i} className="flex gap-3 text-muted-foreground">
                    <CheckCircle2 className="w-6 h-6 text-primary shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Materials */}
      <section className="py-20 bg-muted/30">
        <div className="container px-4 max-w-4xl">
          <div className="space-y-6">
            <h2 className="text-3xl font-bold">
              Materials We Use for Roof Repair
            </h2>
            <p className="text-lg text-muted-foreground">
              A repair is only as good as the materials used to fix it. We use
              heavy-gauge aluminum and galvanized steel for all flashing repairs
              around chimneys and valleys. For shingle replacement, we carry a
              wide inventory of architectural and 3-tab shingles to ensure the
              closest possible color match to your existing roof. We use
              premium, cold-weather grade polyurethane sealants that remain
              flexible in Saskatchewan's freezing winters, ensuring your repair
              won't crack and leak again next season.
            </p>
            <div className="mt-8">
              <img
                src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/a45bf747-d0d6-427b-adfb-d9ba7d5beb9b.png&output=webp&q=60"
                alt="Roofer repairing flashing around a chimney"
                className="rounded-xl shadow-md w-full h-80 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Repair Warning Signs */}
      <section className="py-20 bg-background">
        <div className="container px-4 max-w-4xl">
          <h2 className="text-3xl font-bold text-center mb-12">
            5 Signs You Need Roof Repair
          </h2>
          <div className="space-y-6">
            {[
              {
                title: "Water Stains on Ceilings",
                desc: "Brown or yellow rings on your interior ceilings or walls are the most obvious sign of an active roof leak.",
              },
              {
                title: "Missing or Damaged Shingles",
                desc: "Shingles that are cracked, curled, or completely blown off leave your underlayment and decking exposed to the elements.",
              },
              {
                title: "Granules in Eavestroughs",
                desc: "If your gutters look like they're filled with coarse black sand, your shingles are deteriorating and losing their protective layer.",
              },
              {
                title: "Damaged Flashing",
                desc: "Bent, rusted, or missing metal flashing around chimneys, vents, and valleys is a leading cause of roof leaks.",
              },
              {
                title: "Ice Damming",
                desc: "Thick ridges of solid ice building up at the edge of your roof indicate poor ventilation and insulation, requiring immediate attention.",
              },
            ].map((item, idx) => (
              <div
                key={idx}
                className="flex gap-6 bg-muted/30 p-6 rounded-xl border"
              >
                <div className="text-4xl font-bold text-primary/30 shrink-0">
                  0{idx + 1}
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                  <p className="text-muted-foreground">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cost & Process */}
      <section className="py-20 bg-muted/50">
        <div className="container px-4">
          <div className="grid md:grid-cols-2 gap-12 mb-12">
            <div className="space-y-6">
              <h2 className="text-3xl font-bold">What It Costs</h2>
              <div className="bg-white p-6 rounded-xl border border-primary/20 shadow-sm mb-6">
                <div className="text-sm font-bold text-primary uppercase tracking-wider mb-2">
                  Estimated Investment
                </div>
                <div className="text-3xl font-bold text-foreground">
                  $300 - $3,000+
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  *Cost depends on the severity and location of the damage.
                </p>
              </div>
              <p className="text-lg text-muted-foreground">
                Minor roof repairs in Saskatchewan typically range from $300 to
                $800, such as replacing a few blown-off shingles or resealing a
                flashing. Major repairs, like fixing a valley leak or replacing
                rotted decking, can range from $1,000 to $3,000. We provide a
                clear quote before starting any work. See our{" "}
                <Link
                  to="/roofing-cost-guide-saskatchewan"
                  className="text-accent hover:underline"
                >
                  Roofing Cost Guide
                </Link>{" "}
                for more info.
              </p>
              <div className="mt-8 rounded-2xl overflow-hidden shadow-2xl h-64">
                <img
                  src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/a45bf747-d0d6-427b-adfb-d9ba7d5beb9b.png&output=webp&q=60"
                  alt="Completed roof repair patch"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="space-y-6">
              <h2 className="text-3xl font-bold">Our Process</h2>
              <div className="space-y-6 mt-8">
                {[
                  {
                    title: "Diagnostic Inspection",
                    desc: "We locate the exact source of the intrusion.",
                  },
                  {
                    title: "Clear Explanation",
                    desc: "We show you photos of the damage and explain the fix.",
                  },
                  {
                    title: "Targeted Repair",
                    desc: "Removing damaged layers and repairing from the deck up.",
                  },
                  {
                    title: "Color Matching",
                    desc: "We do our best to match your existing shingle color.",
                  },
                  {
                    title: "Final Test",
                    desc: "Ensuring the area is completely sealed against the elements.",
                  },
                ].map((step, idx) => (
                  <div key={idx} className="flex gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold shrink-0">
                      {idx + 1}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold">{step.title}</h3>
                      <p className="text-muted-foreground mt-1">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <ServiceAreasSection service="Roof Repair" serviceSlug="repair" />

      <section className="py-20 bg-background">
        <div className="container px-4 max-w-4xl">
          <h2 className="text-3xl font-bold text-center mb-12">
            Roof Repair FAQ
          </h2>
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="item-1">
              <AccordionTrigger className="text-lg font-medium text-left">
                How quickly can you fix a leaking roof?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground text-base">
                We prioritize active leaks and can typically dispatch a repair
                crew within 24-48 hours. We also offer emergency tarping to stop
                immediate water ingress.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-2">
              <AccordionTrigger className="text-lg font-medium text-left">
                Why is my roof leaking in the winter?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground text-base">
                Winter leaks in Saskatchewan are often caused by ice
                damming—where melting snow refreezes at the eaves, pushing water
                back up under the shingles.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-3">
              <AccordionTrigger className="text-lg font-medium text-left">
                Can you repair a roof that is 20 years old?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground text-base">
                It depends on the condition of the surrounding shingles. If they
                are extremely brittle, attempting a repair might cause more
                damage. We will assess the roof and give you an honest
                recommendation on whether repair or{" "}
                <Link
                  to="/services/replacement"
                  className="underline text-primary"
                >
                  replacement
                </Link>{" "}
                is the better investment.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      <section className="py-20 bg-primary text-primary-foreground text-center">
        <div className="container px-4 max-w-3xl space-y-8">
          <h2 className="text-3xl font-bold">
            Stop the Leak Before It Spreads
          </h2>
          <p className="text-xl text-primary-foreground/80">
            We serve Saskatoon, Regina, Moose Jaw, Prince Albert, Swift Current,
            Yorkton, Estevan, and Weyburn. Most roof repair projects take less
            than a day once scheduled. Book a free inspection to get started.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
            <Button
              size="lg"
              className="bg-accent text-accent-foreground hover:bg-accent/90"
              asChild
            >
              <Link to="/contact">Schedule a Free Repair Inspection</Link>
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="bg-transparent border-white text-white hover:bg-white/10"
              asChild
            >
              <Link to="/process">See Our Process</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
